// Page Marketplace Mobile - Design unifié
// Header: [Marketplace] [🔄] [👤 Pro/Part] [🔍]
// Onglets: Tous | Rayons | Filtre

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Star, Package, X, Clock, Loader2, Store, User, Heart, MapPin,
  ShoppingCart, Truck, Repeat, HandHeart, SlidersHorizontal, Grid3X3
} from 'lucide-react';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import UnifiedFilterSystem from '../../components/filters/UnifiedFilterSystem';
import MarketplaceCatalogueMenu from '../../components/marketplace/MarketplaceCatalogueMenu';
import { useBuyer } from '../../contexts/BuyerContext';
import { useDemoMode } from '../../components/demo/DemoModeProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types de transaction
const TRANSACTION_TYPES = [
  { id: 'achat', label: 'Achat', icon: ShoppingCart, color: '#F97316' },
  { id: 'location', label: 'Location', icon: Clock, color: '#3B82F6' },
  { id: 'echange', label: 'Échange', icon: Repeat, color: '#8B5CF6' },
  { id: 'don', label: 'Don', icon: HandHeart, color: '#22C55E' },
];

// Carte Produit
function ProductCard({ product, onToggleFavorite, isFavorite }) {
  const navigate = useNavigate();
  const { requireAuth } = useDemoMode();
  const isPro = product.seller_type === 'pro';

  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    if (requireAuth('ajouter aux favoris')) {
      onToggleFavorite(product);
    }
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return 'Gratuit';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const getTransactionBadge = () => {
    const type = TRANSACTION_TYPES.find(t => t.id === product.transaction_type);
    if (!type || type.id === 'achat') return null;
    const Icon = type.icon;
    return (
      <div 
        className="absolute bottom-2 left-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white flex items-center gap-0.5"
        style={{ backgroundColor: type.color }}
      >
        <Icon size={9} />
        {type.label}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100"
      onClick={() => navigate(`/produits/${product.id}`)}
    >
      {/* Image */}
      <div className="relative aspect-square">
        {product.image ? (
          <img 
            src={product.image} 
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <Package size={32} className="text-gray-300" />
          </div>
        )}
        
        {/* Badge Pro/Particulier */}
        <div 
          className={`absolute top-2 left-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold flex items-center gap-0.5 ${
            isPro 
              ? 'bg-[#1E3A5F] text-white' 
              : 'bg-orange-100 text-orange-700'
          }`}
        >
          {isPro ? (
            <>
              <Store size={9} />
              Pro
            </>
          ) : (
            <>
              <User size={9} />
              Particulier
            </>
          )}
        </div>

        {/* Bouton Favoris */}
        <button
          onClick={handleToggleFavorite}
          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 shadow-sm flex items-center justify-center"
        >
          <Heart 
            size={14} 
            className={isFavorite ? 'text-red-500 fill-red-500' : 'text-gray-400'} 
          />
        </button>
        
        {/* Badge Transaction */}
        {getTransactionBadge()}
        
        {/* Livraison gratuite */}
        {isPro && product.delivery_free && (
          <div className="absolute bottom-2 right-2 bg-green-600 text-white px-1.5 py-0.5 text-[9px] font-bold rounded flex items-center gap-0.5">
            <Truck size={9} />
            Gratuit
          </div>
        )}
      </div>
      
      {/* Infos */}
      <div className="p-2.5">
        <h3 className="font-medium text-xs text-gray-900 line-clamp-2 leading-tight min-h-[32px]">
          {product.name}
        </h3>
        
        {/* Localisation pour particuliers */}
        {!isPro && product.ville && (
          <p className="text-[10px] text-gray-500 flex items-center gap-0.5 mt-1">
            <MapPin size={9} />
            {product.ville}
          </p>
        )}
        
        {/* Note pour pros */}
        {isPro && product.rating > 0 && (
          <div className="flex items-center gap-1 mt-1">
            <Star size={10} className="text-amber-400 fill-amber-400" />
            <span className="text-[10px] text-gray-600">{product.rating}</span>
            <span className="text-[10px] text-gray-400">({product.reviews_count || 0})</span>
          </div>
        )}
        
        {/* Prix */}
        <div className="flex items-baseline gap-1 mt-1.5">
          <span className={`text-sm font-bold ${product.price === 0 ? 'text-green-600' : 'text-orange-600'}`}>
            {formatPrice(product.price)}
          </span>
          {product.negociable && (
            <span className="text-[9px] text-gray-400">Négociable</span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function MobileEntreParticuliersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  
  // Filtres - FORCÉ PARTICULIER
  const [activeTab, setActiveTab] = useState('tous'); // tous, filtre, rayons
  const sellerType = 'particulier'; // FORCÉ PARTICULIER - pas de changement possible
  const [showFullscreenSearch, setShowFullscreenSearch] = useState(false);
  const [showRayonsMenu, setShowRayonsMenu] = useState(false);
  const [showFullFilters, setShowFullFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [recentSearches, setRecentSearches] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const searchInputRef = useRef(null);
  
  // Contexte acheteur
  const { 
    toggleFavorite: contextToggleFavorite, 
    isFavorite: contextIsFavorite,
  } = useBuyer();
  
  // Charger les recherches récentes
  useEffect(() => {
    try {
      const saved = localStorage.getItem('marketplace_recent_searches');
      if (saved) setRecentSearches(JSON.parse(saved).slice(0, 5));
    } catch {}
  }, []);
  
  // Focus sur l'input quand la recherche s'ouvre
  useEffect(() => {
    if (showFullscreenSearch && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 250);
    }
  }, [showFullscreenSearch]);
  
  // Charger les produits
  useEffect(() => {
    fetchProducts();
  }, [sellerType, searchQuery]);
  
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      // FORCÉ PARTICULIER
      params.set('seller_type', 'particulier');
      params.set('per_page', '20');
      
      const res = await fetch(`${API_URL}/api/marketplace/products?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
        setTotalProducts(data.total || 0);
      } else {
        setProducts([]);
        setTotalProducts(0);
      }
    } catch (e) {
      console.error('Error fetching products:', e);
      setProducts([]);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  };
  
  // Sauvegarder une recherche
  const saveRecentSearch = (query) => {
    if (!query || query.trim().length < 2) return;
    const trimmed = query.trim();
    const updated = [trimmed, ...recentSearches.filter(s => s.toLowerCase() !== trimmed.toLowerCase())].slice(0, 5);
    setRecentSearches(updated);
    try {
      localStorage.setItem('marketplace_recent_searches', JSON.stringify(updated));
    } catch {}
  };
  
  // Effacer les recherches récentes
  const clearRecentSearches = () => {
    setRecentSearches([]);
    try { localStorage.removeItem('marketplace_recent_searches'); } catch {}
  };
  
  // Soumettre la recherche
  const handleSearchSubmit = (query) => {
    if (query && query.trim()) {
      saveRecentSearch(query);
      setSearchQuery(query.trim());
      const params = new URLSearchParams(searchParams);
      params.set('q', query.trim());
      setSearchParams(params, { replace: true });
    }
    setShowFullscreenSearch(false);
  };
  
  // Toggle favori
  const toggleFavorite = (product) => {
    contextToggleFavorite({
      id: product.id,
      title: product.name,
      price: product.price,
      image: product.image
    });
  };

  // Gérer le clic sur les onglets
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (tab === 'rayons') {
      setShowRayonsMenu(true);
    } else if (tab === 'filtre') {
      setShowFullFilters(true);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50" style={{ paddingBottom: '80px' }}>
      {/* Header Marketplace */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="flex items-center justify-between h-14 px-3">
          <div className="flex items-center gap-0.5">
            <h1 className="text-lg font-bold text-[#1E3A5F]">Entre particuliers</h1>
          </div>
          
          {/* Boutons droite : Loupe uniquement (Particulier forcé) */}
          <div className="flex items-center gap-0.5">
            {/* Loupe */}
            <button 
              onClick={() => setShowFullscreenSearch(true)}
              className="p-2 hover:bg-gray-100 rounded-full"
              data-testid="marketplace-search-btn"
            >
              <Search size={20} className="text-gray-600" />
            </button>
          </div>
        </div>
        
        {/* Onglets Tous / Filtre / Rayons */}
        <div className="flex border-b border-gray-100">
          <button 
            onClick={() => handleTabClick('tous')}
            className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${
              activeTab === 'tous' 
                ? 'text-orange-600 border-b-2 border-orange-500' 
                : 'text-gray-500'
            }`}
            data-testid="marketplace-tab-tous"
          >
            <Package size={14} />
            Tous
          </button>
          <button 
            onClick={() => handleTabClick('filtre')}
            className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${
              activeTab === 'filtre' 
                ? 'text-orange-600 border-b-2 border-orange-500' 
                : 'text-gray-500'
            }`}
            data-testid="marketplace-tab-filtre"
          >
            <SlidersHorizontal size={14} />
            Filtre
          </button>
          <button 
            onClick={() => handleTabClick('rayons')}
            className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${
              activeTab === 'rayons' 
                ? 'text-orange-600 border-b-2 border-orange-500' 
                : 'text-gray-500'
            }`}
            data-testid="marketplace-tab-rayons"
          >
            <Grid3X3 size={14} />
            Rayons
          </button>
        </div>
      </header>
      
      {/* Contenu */}
      <div className="p-3">
        {/* Compteur résultats */}
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-gray-500">
            {totalProducts} produit{totalProducts > 1 ? 's' : ''}
            {searchQuery && ` pour "${searchQuery}"`}
            {sellerType !== 'all' && ` • ${sellerType === 'pro' ? 'Pro' : 'Particulier'}`}
          </p>
          {(searchQuery || sellerType !== 'all') && (
            <button 
              onClick={() => {
                setSearchQuery('');
                setSellerType('all');
                const params = new URLSearchParams();
                setSearchParams(params, { replace: true });
              }}
              className="text-xs text-orange-600 font-medium"
            >
              Réinitialiser
            </button>
          )}
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Package size={32} className="text-gray-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              Aucun produit trouvé
            </h3>
            <p className="text-sm text-gray-500">
              {searchQuery 
                ? `Aucun résultat pour "${searchQuery}"`
                : 'Modifie tes critères de recherche'}
            </p>
          </div>
        ) : (
          /* Grille Produits */
          <div className="grid grid-cols-2 gap-3">
            {products.map((product) => (
              <ProductCard 
                key={product.id} 
                product={product}
                onToggleFavorite={toggleFavorite}
                isFavorite={contextIsFavorite(product.id)}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* ══════════════════════════════════════════════════════════════
          RECHERCHE FULLSCREEN
      ══════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showFullscreenSearch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: '#ffffff',
              zIndex: 99999,
            }}
          >
            {/* X fermer */}
            <div 
              className="flex items-center justify-end"
              style={{ 
                padding: '8px 12px',
                paddingTop: 'max(8px, env(safe-area-inset-top, 8px))',
              }}
            >
              <button
                onClick={() => setShowFullscreenSearch(false)}
                style={{ width: '32px', height: '32px' }}
                data-testid="fullscreen-search-close"
              >
                <X size={24} className="text-[#64748B]" strokeWidth={2} />
              </button>
            </div>
            
            {/* Barre de recherche - Style stadium avec loupe orange */}
            <div className="px-3 pb-3">
              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSearchSubmit(searchQuery);
                }}
                className="flex items-center w-full"
                style={{
                  height: '56px',
                  border: '2px solid #E8650A',
                  borderRadius: '28px',
                  background: '#F8F9FA',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {/* Loupe orange à gauche */}
                <motion.button
                  type="submit"
                  initial={{ x: 320, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 280, damping: 24, delay: 0.03 }}
                  className="flex items-center justify-center flex-shrink-0"
                  style={{ 
                    width: '56px', 
                    height: '56px',
                    background: '#E8650A',
                    borderRadius: '26px 0 0 26px',
                    position: 'relative',
                    left: '-2px',
                  }}
                  data-testid="fullscreen-search-submit"
                >
                  <Search size={24} className="text-white" strokeWidth={2} />
                </motion.button>
                
                {/* Input */}
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Produit, marque, référence..."
                  className="flex-1 bg-transparent text-[#333] placeholder-[#999] focus:outline-none"
                  style={{ fontSize: '16px', paddingLeft: '12px', paddingRight: '16px' }}
                  autoFocus
                  data-testid="fullscreen-search-input"
                />
                
                {/* Clear */}
                {searchQuery && (
                  <button 
                    type="button" 
                    onClick={() => setSearchQuery('')}
                    className="flex items-center justify-center mr-4"
                    style={{ width: '22px', height: '22px', background: '#C9CDD4', borderRadius: '50%' }}
                  >
                    <X size={14} className="text-white" strokeWidth={2.5} />
                  </button>
                )}
              </form>
            </div>
            
            {/* Bouton "Trouve ton produit en 3 clics" */}
            <motion.div 
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="px-4 py-4"
            >
              <button
                onClick={() => {
                  setShowFullscreenSearch(false);
                  setTimeout(() => setShowFullFilters(true), 150);
                }}
                className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-semibold text-white transition-all active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)',
                  boxShadow: '0 4px 14px rgba(30, 58, 95, 0.25)',
                }}
                data-testid="mobile-search-open-filters"
              >
                <SlidersHorizontal size={20} strokeWidth={2} />
                <span style={{ fontSize: '15px' }}>Trouve ton produit en 3 clics</span>
              </button>
            </motion.div>
            
            {/* Contenu - Recherches récentes */}
            <div className="p-4 bg-white flex-1 overflow-y-auto">
              <p className="text-[11px] text-[#E8650A] uppercase font-semibold tracking-wide mb-4">
                Recherche dans : Marketplace
              </p>
              
              {/* Recherches récentes */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-[#9CA3AF] uppercase font-semibold tracking-wide">
                    Recherches récentes
                  </p>
                  {recentSearches.length > 0 && (
                    <button
                      onClick={clearRecentSearches}
                      className="text-[11px] text-[#E8650A] font-medium"
                    >
                      Effacer
                    </button>
                  )}
                </div>
                
                {recentSearches.length > 0 ? (
                  <div className="space-y-1">
                    {recentSearches.map((search, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSearchSubmit(search)}
                        className="w-full flex items-center gap-3 py-3 px-2 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <Clock size={16} className="text-[#9CA3AF]" />
                        <span className="text-[14px] text-[#374151]">{search}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Search size={32} className="text-[#E5E7EB] mb-3" />
                    <p className="text-[#9CA3AF] text-sm">Aucune recherche récente</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Menu Rayons */}
      <MarketplaceCatalogueMenu 
        isOpen={showRayonsMenu}
        onClose={() => {
          setShowRayonsMenu(false);
          setActiveTab('tous');
        }}
      />
      
      {/* UnifiedFilterSystem - "Trouve ton produit en 3 clics" */}
      <UnifiedFilterSystem 
        context="marketplace"
        isOpen={showFullFilters}
        onClose={() => {
          setShowFullFilters(false);
          setActiveTab('tous');
        }}
        filters={activeFilters}
        onFiltersChange={setActiveFilters}
        resultsCount={totalProducts}
        onApply={() => {
          setShowFullFilters(false);
          setActiveTab('tous');
        }}
      />
      
      {/* Bottom Nav */}
      {/* MobileBottomNav est géré globalement par MobileNavWrapper dans App.js */}
    </div>
  );
}
